/**
 * Custom dimension names attached to UI telemetry.
 *
 * These deliberately mirror the BFF API's `app.*` schema so UI and API telemetry can be
 * joined in Kusto on the same dimension names (for example `app.tenant.id`).
 *
 * Only pseudonymous identifiers and application-defined enums belong on these dimensions —
 * never email addresses, UPNs, display names, or user-entered content.
 */
export const TelemetryDimensions = {
    /** Entra object id (`oid`; `sub` fallback for non-AAD accounts). Pseudonymous, never a UPN. */
    UserId: 'app.user.id',

    /** Target tenant when an operation has exactly one tenant in scope. */
    TenantId: 'app.tenant.id',

    /** Tenant the signed-in account belongs to. */
    AuthTenantId: 'app.auth.tenant_id',

    /** Number of distinct target tenants in an operation. */
    TenantCount: 'app.tenant.count',

    /** Deployment entity type. */
    EntityType: 'app.entity.type',

    /** Number of entities in a deployment operation. */
    EntityCount: 'app.entity.count',

    /** Deployment batch identifier returned by the BFF. */
    DeploymentBatchId: 'app.deployment.batch_id',

    /** Browser session identifier generated on client start. */
    SessionId: 'app.session.id',

    /** Deployment environment (development/preproduction/production). */
    Environment: 'app.environment',

    /** Feature area, from {@link FeatureName}. */
    Feature: 'app.feature',

    /** Action performed within the feature. */
    Action: 'app.action',

    /** Component that raised the event. */
    Component: 'app.component',

    /** Outcome of the action (success/failure). */
    Result: 'app.result',

    /** Proactive alert classification. */
    AlertType: 'app.alert.type',

    /** Proactive alert severity. */
    Severity: 'app.alert.severity',

    /** Component or subsystem the alert originated from. */
    Source: 'app.alert.source',

    /** Short, non-personal description of the alert condition. */
    Description: 'app.alert.description'
} as const;

/**
 * Standard action outcomes used with {@link TelemetryDimensions.Result}.
 */
export const TelemetryResult = {
    Success: 'Success',
    Failure: 'Failure'
} as const;

/**
 * Names of the custom events this application emits.
 *
 * Page views, route changes, HTTP dependencies and unhandled exceptions are collected
 * automatically by the Application Insights SDK and intentionally have no entry here —
 * re-emitting them would duplicate telemetry and break correlation.
 */
export const TelemetryEventName = {
    /** How users interact with a specific feature (see {@link FeatureName}). */
    FeatureUsage: 'FeatureUsage',

    /** A degraded or blocked condition the UI proactively surfaces to the user. */
    ProActiveAlert: 'ProActiveAlert'
} as const;

/**
 * Application feature names for telemetry
 * Used to categorize telemetry events by feature/module for better analytics and filtering
 */
export enum FeatureName {
    /** Authentication and login related events */
    Authentication = 'Authentication',

    /** Main dashboard views and interactions */
    Dashboard = 'Dashboard',

    /** Application settings and configuration changes */
    Settings = 'Settings',

    /** Microsoft Purview integration and data governance features */
    Purview = 'Purview',
}